import type { Module, TFunction, i18n, TOptions } from "i18next";

interface PluralFallbackModule extends Module {
  type: "3rdParty";
  init(i18next: i18n): void;
}

/**
 * This plugin provides a backward-compatibility layer for i18next to handle
 * pluralization key changes introduced in i18next v21.
 *
 * --- Why is this needed? ---
 *
 * In i18next v21, the pluralization key suffix was changed to align with the
 * Unicode CLDR (Common Locale Data Repository) standard. This was a significant
 * breaking change for many users.
 *
 *   - Old Format (v3 JSON): `key_plural`
 *   - New Format (v4 JSON): `key_one`, `key_other`, `key_few`, `key_many`, etc.
 *
 * For English and many other languages, the simple plural form `key_plural` was
 * replaced by `key_other`. This meant that all existing translation files with
 * thousands of `_plural` keys would break upon upgrading. In addition, we have
 * many thousands of keys stored in the database that would need to be updated.
 *
 * i18next initially provided a `compatibilityJSON: 'v3'` option to maintain the old
 * behavior. However, this option has been deprecated and removed in later versions.
 *
 * This plugin solves the problem by intercepting translation and existence checks.
 * We first check first if the user is asking for a count. If so, we follow the old
 * plural rules, first, then the new (for when we start upgrading), then fall back
 * to the default i18next behavior.
 *
 * - i18next Migration Guide (discusses the v3 to v4 JSON format change):
 *   https://www.i18next.com/misc/migration-guide#v23.x.x-to-v24.0.0
 */
export const PluralFallbackPlugin: PluralFallbackModule = {
  type: "3rdParty",
  init(i18next: i18n): void {
    const originalT: any = i18next.t.bind(i18next);

    // Override the translate function
    // eslint-disable-next-line complexity, @typescript-eslint/consistent-type-assertions
    i18next.t = function (key: string | string[], options?: TOptions | string) {
      const keysToTranslate = Array.isArray(key) ? key : [key];

      const hasCount =
        options &&
        typeof options === "object" &&
        options.count &&
        typeof options.count === "number";

      if (!hasCount || options?.count === 1) {
        return originalT(key, options);
      }

      // ⭐ i18next might get `content:key_plural`, but if it's not found, will
      //     return `key_plural`. That's why we `.endsWith` below, instead of
      //     `.includes` or `.startsWith` or `===`.

      for (const key of keysToTranslate) {
        const alreadyHasSuffix =
          key.endsWith("_plural") || key.endsWith("_other");

        if (alreadyHasSuffix) {
          const suffixResult = originalT(key, options);
          if (!key.endsWith(suffixResult)) {
            return suffixResult;
          }
        }

        // First, try _plural
        const keyWithPluralSuffix = addOrReplaceSuffix(key, "_plural");
        const pluralResult = originalT(keyWithPluralSuffix, options);
        if (!keyWithPluralSuffix.endsWith(pluralResult)) {
          return pluralResult;
        }

        // If that fails, try _other
        const keyWithOtherSuffix = addOrReplaceSuffix(key, "_other");
        const otherResult = originalT(keyWithOtherSuffix, options);
        if (!keyWithOtherSuffix.endsWith(otherResult)) {
          return otherResult;
        }
      }

      // Fallback, give it back to i18next
      return originalT(key, options);
    } as TFunction;
  },
};

const suffixes = ["_plural", "_other"];
function addOrReplaceSuffix(key: string, suffix: string) {
  for (const s of suffixes) {
    if (key.endsWith(s)) {
      return key.slice(0, -s.length) + suffix;
    }
  }
  return key + suffix;
}
